/** Formatting helpers for the UI. Money is integer cents everywhere; never parse user currency strings. */

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });
const integer = new Intl.NumberFormat("en-US");

export function formatCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return "—";
  return usd.format(cents / 100);
}

/** Metric value: null means "not available" and is shown as a dash (docs/01 section 6). */
export function formatMetric(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return integer.format(value);
}

export function formatCount(value: number): string {
  return integer.format(value);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function formatUtcDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.toISOString().slice(0, 16).replace("T", " ")} UTC`;
}

export function formatUtcDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toISOString().slice(0, 10);
}

export function formatRelative(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Math.max(0, now - t);
  const s = Math.round(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  const d = Math.round(h / 24);
  return `${d} days ago`;
}

export function formatSeconds(ms: number): string {
  return `${Math.max(0, Math.ceil(ms / 1000))}s`;
}

/** The seven home levels (ADR-0005). Legacy ids from before the migration map to their new names. */
export const HOME_STAGE_LABELS: Record<string, string> = {
  tent: "Tent",
  log_cabin: "Log cabin",
  trailer_home: "Trailer home",
  small_house: "Small house",
  medium_house: "Medium house",
  mansion: "Mansion",
  castle: "Castle",
  regular_house: "Small house",
  large_house: "Medium house",
};

export const TEMPLATE_LABELS: Record<string, string> = {
  small_shop: "Small shop",
  corner_shop: "Corner shop",
  office_block: "Office block",
  modern_midrise: "Modern mid-rise",
  colorful_tower: "Colorful tower",
  landmark_tower: "Landmark tower",
};

export function titleCaseSlug(slug: string): string {
  return slug
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Slug used by the seeded demo destinations: lower-case, "&" -> "and", punctuation stripped. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
